import { BET, BETS, CELLARS, CRYPT_ASCUAS, DEEP_CRYPT, DUNGEON, DUNGEON_REWARD, INCURSION, INCURSION_SITES, MAZE, SAGA, SIMA, SAGA_CHAPTERS, SAGA_SWITCH, SIDE, SIDES } from '@somnhold/content';
import {
  betOpen,
  betsOf,
  cellarsOpen,
  cryptTierOpen,
  deepCryptOpen,
  dungeonOpen,
  eraOf,
  groupLevel,
  groupReached,
  mazeOpen,
  mazeRecord,
  simaOpen,
  simaRecord,
  sagaBlocked,
  sagaOf,
  sideBlocked,
  sideOf,
  sideOpensAt,
  type CampaignState,
  type OutingRun,
  type OutingTally,
  type PlayerState,
} from '@somnhold/sim';
import { getLang, t } from '../i18n.ts';
import { h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';
import type { CampaignService } from './service.ts';

/**
 * Salidas: every place a hero can go out to, on one screen. First what still pays today and who is gathering
 * live; then one card per outing (what it is, from what level, its state, your record and its reward); a card
 * opens its sheet with your numbers against the group's, the history (when, with whom, how it ended) and the
 * way in. Plain DOM, no WebGL: the titan's map is only built when its sheet sends you to it.
 */

export interface OutingsGo {
  /** The titan's map, to pick an incursion. */
  map: () => void;
  /** A crypt, with the absent friends picked to come along as spectres. */
  crypt: (tier: 1 | 2 | 3, ghosts: string[]) => void;
  /** The chronicle: the Turtle's saga, the other stories and the bets. */
  story: () => void;
  /** The full cards of the group outings (their trophy, who gathers, the way in). */
  cellars: () => HTMLElement | null;
  furrows: () => HTMLElement | null;
  /** The Chasm's card (its marks, where to start, the way down). */
  sima: () => HTMLElement | null;
}

type Cat = 'story' | 'inc' | 'crypt' | 'group' | 'bet';
type State = 'todo' | 'done' | 'repeat' | 'lock' | 'wait';

interface Outing {
  id: string;
  cat: Cat;
  sigil: string;
  kind: string;
  title: string;
  state: State;
  status: string;
  line: string;
  /** Steps in all, done, and whether the next is under way. */
  meter?: [number, number, boolean?];
  /** Outing-log keys this card counts (OutingKey). */
  keys: string[];
  rewards: [string, string][];
  /** Its line in "Today", when it pays today or was cashed in today. */
  today?: string;
  go?: { label: string; test: string; run: () => void; note?: string };
  /** More in the sheet: the outing's own card (trophy, gathering, the way in). */
  more?: () => HTMLElement | null;
  /** Above the way in: a choice it takes (the crypts' spectres). */
  pick?: () => HTMLElement;
}

const CATS: Cat[] = ['story', 'inc', 'crypt', 'group', 'bet'];

/** Emblems, one per outing (stroke drawings in the family's colour). */
const SIGIL: Record<string, string> = {
  turtle: '<path d="M4 15c0-5 3.6-8 8-8s8 3 8 8z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8 15l1.5-4h5L16 15M12 7v4" stroke="currentColor" stroke-width="1.3" fill="none"/><circle cx="21" cy="13" r="1.6" fill="currentColor"/><path d="M6 15l-1 3M18 15l1 3" stroke="currentColor" stroke-width="1.6"/>',
  book: '<path d="M4 5h6a2 2 0 0 1 2 2v12a2 2 0 0 0-2-2H4zM20 5h-6a2 2 0 0 0-2 2v12a2 2 0 0 1 2-2h6z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>',
  faces: '<path d="M5 5h14v6a7 7 0 0 1-14 0z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M8.5 10h2M13.5 10h2M10 15c1.3.8 2.7.8 4 0" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  horn: '<path d="M4 18c6 0 12-4 14-12l2 1c-1 9-8 14-16 14z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M8 17l1 2M12 15l1.3 1.8M15.5 12l1.6 1.2" stroke="currentColor" stroke-width="1.4"/>',
  burn: '<path d="M12 3c1 4 5 5 5 10a5 5 0 0 1-10 0c0-3 2-4 2-6 1.5 1 2 2 2 3 0-3 1-5 1-7z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>',
  rails: '<path d="M5 8h14l-2 8H7z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><circle cx="9" cy="18" r="1.6" fill="currentColor"/><circle cx="15" cy="18" r="1.6" fill="currentColor"/><path d="M2 21h20" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  sima: '<path d="M4 6h16M8 6V3h8v3" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M12 6v9" stroke="currentColor" stroke-width="1.3" stroke-dasharray="2 1.5"/><path d="M5 15c3 0 4 2 7 2s4-2 7-2c-1 4-3 6-7 6s-6-2-7-6z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/>',
  well: '<path d="M5 12h14v8H5z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M5 16h14M6 12V5h12v7M4 5h16M12 5v3" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linecap="round"/><path d="M10.5 8h3l-.5 2.5h-2z" fill="currentColor"/>',
  shore: '<path d="M3 15c2-2 4-2 6 0s4 2 6 0 4-2 6 0" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M3 19c2-2 4-2 6 0s4 2 6 0 4-2 6 0" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M8 12V5M8 5l6 5H8M16 12V8" stroke="currentColor" stroke-width="1.5" fill="none" stroke-linejoin="round"/>',
  lid: '<path d="M2.5 12c3-4.5 6-6.5 9.5-6.5s6.5 2 9.5 6.5c-3 4.5-6 6.5-9.5 6.5S5.5 16.5 2.5 12z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M4.5 12.5c2.4 2.6 4.8 3.8 7.5 3.8s5.1-1.2 7.5-3.8" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/><path d="M7 15.6l-.9 1.8M12 16.6v2M17 15.6l.9 1.8" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>',
  crypt1: '<path d="M5 21V10a7 7 0 0 1 14 0v11" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M9 21v-9M12 21V9.5M15 21v-9M5 21h14" stroke="currentColor" stroke-width="1.4"/>',
  crypt2: '<path d="M5 21V10a7 7 0 0 1 14 0v11H5z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M12 19c-2 0-3-1.2-3-2.8 0-1.6 1.6-2.4 1.6-3.8 1 .6 1.4 1.4 1.4 2 .4-.8.8-1.4.8-2.4 1.4 1 2.2 2.4 2.2 4.2 0 1.6-1 2.8-3 2.8z" fill="currentColor"/>',
  crypt3: '<path d="M5 21V10a7 7 0 0 1 14 0v11" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M9 21v-3h6v3M10 18v-3h4v3M11 15v-2h2v2" stroke="currentColor" stroke-width="1.3" fill="none"/>',
  bell: '<path d="M6 17c1-1 1-3 1-6a5 5 0 0 1 10 0c0 3 0 5 1 6z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M4 17h16M12 4V2M10.5 20a1.6 1.6 0 0 0 3 0" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>',
  maze: '<path d="M3 3h18v18H3zM7 7h10v10H7zM11 11h2v2h-2zM12 3v4M7 12H3M21 12h-4M12 17v4" fill="none" stroke="currentColor" stroke-width="1.5"/>',
  plaque: '<path d="M4 6h16v10H4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M7 9.5h10M7 12.5h7" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/><path d="M8 16v5M16 16v5" stroke="currentColor" stroke-width="1.6"/><circle cx="5.8" cy="7.8" r=".7" fill="currentColor"/><circle cx="18.2" cy="7.8" r=".7" fill="currentColor"/>',
  barrel: '<path d="M7 3h10c1.5 3 1.5 15 0 18H7C5.5 18 5.5 6 7 3z" fill="none" stroke="currentColor" stroke-width="1.6"/><path d="M6 8h12M6 16h12" stroke="currentColor" stroke-width="1.4"/>',
};
const sigil = (k: string): HTMLElement => h('span.out-sigil', { html: `<svg viewBox="0 0 24 24" aria-hidden="true">${SIGIL[k] ?? SIGIL.book}</svg>` });

const pct = (bp: number): string => `${Math.round(bp / 100)} %`;

/** The friends picked to come down the crypts as spectres on this device (none until picked). */
const spectresKey = (id: string): string => `somnhold.cryptSpectres.${id}`;
const pickedSpectres = (id: string): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(spectresKey(id)) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * Which absent friends go down the crypt as spectres (as in an incursion): chips, one per friend, the choice kept for
 * the next time. `get` gives the ones picked who are still in the group.
 */
const spectrePicker = (svc: CampaignService): { el: () => HTMLElement; get: () => string[] } => {
  const s = svc.state;
  const others = s.players.filter((p) => p.id !== svc.me && !p.left).slice(0, DUNGEON.maxHeroes - 1);
  const picked = new Set(pickedSpectres(s.id).filter((id) => others.some((p) => p.id === id)));
  const online = new Set(svc.presence()?.online ?? []);
  const el = (): HTMLElement => {
    const count = h('span.small', {}, t('crypt.spectres.count', { n: picked.size, of: others.length }));
    const chips = others.map((p) => {
      const b = h(
        `button.ghost-chip${picked.has(p.id) ? '.on' : ''}`,
        { type: 'button', 'aria-pressed': String(picked.has(p.id)), 'data-test': 'crypt-spectre', 'data-pid': p.id },
        h('span', { html: ICONS.ghost }),
        p.displayName,
        online.has(p.id) ? h('i.online-dot', { title: t('inc.online') }) : '',
      );
      b.addEventListener('click', () => {
        if (picked.has(p.id)) picked.delete(p.id);
        else picked.add(p.id);
        b.classList.toggle('on', picked.has(p.id));
        b.setAttribute('aria-pressed', String(picked.has(p.id)));
        count.textContent = t('crypt.spectres.count', { n: picked.size, of: others.length });
        try {
          localStorage.setItem(spectresKey(s.id), JSON.stringify([...picked]));
        } catch {
          /* fine */
        }
      });
      return b;
    });
    return h(
      'div.crypt-spectres',
      { 'data-test': 'crypt-spectres' },
      h('div.row', {}, h('span.label', {}, t('crypt.spectres.title')), count),
      h('div.ghost-chips', {}, ...chips),
      h('p.small', {}, t('crypt.spectres.hint', { n: Math.round(DUNGEON.spectreShareBp / 100) })),
    );
  };
  return { el, get: () => others.filter((p) => picked.has(p.id)).map((p) => p.id) };
};
const nameOf = (s: CampaignState, pid: string): string => s.players.find((p) => p.id === pid)?.displayName ?? '—';

/** Every outing as the screen shows it, from the campaign as it is now. */
export const outingsOf = (svc: CampaignService, go: OutingsGo): Outing[] => {
  const s = svc.state;
  const me = s.players.find((p) => p.id === svc.me) as PlayerState;
  const day = svc.day();
  const g = groupLevel(s, s.clock);
  const online = svc.kind === 'server';
  const out: Outing[] = [];

  // The Turtle's saga (and, riding its chronicle, the other stories and the bets).
  const story = SAGA_SWITCH.on;
  const sg = sagaOf(s);
  const total = SAGA_CHAPTERS.length;
  const block = sagaBlocked(s, day);
  const ch = SAGA_CHAPTERS[sg.chapter];
  const chTitle = ch ? t(`saga.${ch.id}.title`) : '';
  if (story) out.push({
    id: 'saga',
    cat: 'story',
    sigil: 'book',
    kind: t('out.kind.saga'),
    title: t('out.saga.title'),
    ...(block === 'over'
      ? { state: 'done' as State, status: t('out.st.over') }
      : block === 'level'
        ? { state: 'lock' as State, status: t('out.st.group', { n: ch?.minGroup ?? 0 }) }
        : block === 'era'
          ? { state: 'lock' as State, status: t('out.st.era', { n: ch?.minEra ?? 0 }) }
          : block === 'resting'
            ? { state: 'wait' as State, status: t('out.st.resting') }
            : block === 'campaignLost'
              ? { state: 'lock' as State, status: t('out.st.lost') }
              : { state: 'todo' as State, status: t('out.st.play') }),
    line:
      block === 'over'
        ? t('out.saga.over', { n: total })
        : block === 'level'
          ? t('out.saga.lineLevel', { done: sg.chapter, n: total, title: chTitle, lv: ch?.minGroup ?? 0, g })
          : t('out.saga.line', { done: sg.chapter, n: total, ch: sg.chapter + 1, title: chTitle }),
    meter: [total, sg.chapter, sg.act > 0],
    keys: ['saga'],
    rewards: [[String(SAGA.xp), t('out.rw.xpChapter')], [t('out.rw.unique'), t('out.rw.uniqueOf')]],
    ...(block === null ? { today: t('out.today.saga', { ch: sg.chapter + 1 }) } : {}),
    go: { label: t('out.go.story'), test: 'saga-btn', run: go.story },
  });

  // The other stories.
  // By level: the list keeps El pozo last for the fights' seeds, but it opens first.
  for (const def of story ? [...SIDES].sort((a, b) => (a.chapters[0]?.minGroup ?? 0) - (b.chapters[0]?.minGroup ?? 0)) : []) {
    const st = sideOf(s, def.id);
    const b = sideBlocked(s, def.id);
    const c = def.chapters[st.chapter];
    const n = def.chapters.length;
    const cTitle = c ? t(`side.${def.id}.${c.id}.title`) : '';
    const hours = Math.max(1, Math.ceil((sideOpensAt(s, def.id) - svc.now()) / 3_600_000));
    const res = SIDE.reward[def.difficulty];
    out.push({
      id: `side:${def.id}`,
      cat: 'story',
      sigil: def.id in SIGIL ? def.id : 'book',
      kind: t('out.kind.side', { d: t(`side.diff.${def.difficulty}`) }),
      title: t(`side.${def.id}.title`),
      ...(b === 'over'
        ? { state: 'done' as State, status: t('out.st.over') }
        : b === 'level'
          ? { state: 'lock' as State, status: t('out.st.group', { n: c?.minGroup ?? 0 }) }
          : b === 'soon'
            ? { state: 'wait' as State, status: t('out.st.soon') }
            : b === 'wait'
              ? { state: 'wait' as State, status: t('out.st.hours', { h: hours }) }
              : b === 'campaignLost'
                ? { state: 'lock' as State, status: t('out.st.lost') }
                : { state: 'todo' as State, status: t('out.st.play') }),
      line:
        b === 'over'
          ? t('out.side.over', { n })
          : b === 'level'
            ? t('out.side.lineLevel', { ch: st.chapter + 1, n, title: cTitle, lv: c?.minGroup ?? 0, left: (c?.minGroup ?? 0) - g })
            : t('out.side.line', { ch: st.chapter + 1, n, title: cTitle, act: st.act + 1 }),
      meter: [n, st.chapter, st.act > 0],
      keys: [`side:${def.id}`],
      rewards: [[String(SIDE.xp), t('out.rw.xp')], [String(res.wood + res.stone + res.crystal + res.dust), t('out.rw.storage')]],
      ...(b === null ? { today: t('out.today.side', { ch: st.chapter + 1 }) } : {}),
      go: { label: t('out.go.story'), test: `side-go-${def.id}`, run: go.story },
    });
  }

  // Incursions.
  const done = me.incursionDay === day ? me.incursionsToday : 0;
  const left = Math.max(0, INCURSION.fullPerDay - done);
  const sites = INCURSION_SITES.filter((x) => (x.minEra ?? 0) <= eraOf(s)).length;
  const won = (k: 'won' | 'won2' | 'won3'): number => s.explored?.[k]?.length ?? 0;
  out.push({
    id: 'inc',
    cat: 'inc',
    sigil: 'turtle',
    kind: t('out.kind.inc'),
    title: t('out.inc.title'),
    state: left ? 'todo' : 'repeat',
    status: left ? t('out.st.incLeft', { n: left }) : t('out.st.incReduced'),
    line: t('out.inc.line', { done, max: INCURSION.fullPerDay, a: won('won'), b: won('won2'), c: won('won3'), n: sites }),
    meter: [INCURSION.fullPerDay, done],
    keys: ['inc1', 'inc2', 'inc3'],
    rewards: [[t('out.rw.res'), t('out.rw.ofPlace')], [String(INCURSION.rewardXp), t('out.rw.xp')]],
    ...(left ? { today: t('out.today.inc', { n: left, done, max: INCURSION.fullPerDay }) } : {}),
    go: { label: t('out.go.map'), test: 'outing-map', run: go.map },
  });

  // The crypts: alone, with friends live, or with absent friends' spectres.
  const spectres = spectrePicker(svc);
  const others = s.players.some((p) => p.id !== svc.me && !p.left);
  const crypt = (tier: 1 | 2 | 3, open: boolean, lockText: string, claimedDay: number | undefined, line: string, rewards: [string, string][]): void => {
    const claimed = claimedDay === day;
    out.push({
      id: `crypt${tier}`,
      cat: 'crypt',
      sigil: `crypt${tier}`,
      kind: t('out.kind.crypt', { n: ['I', 'II', 'III'][tier - 1] as string }),
      title: t(`out.crypt${tier}.title`),
      state: !open ? 'lock' : claimed ? 'done' : 'todo',
      status: !open ? lockText : claimed ? t('out.st.today') : t('out.st.chest'),
      line,
      keys: [`crypt${tier}`],
      rewards,
      ...(open ? { today: claimed ? t('out.today.claimed') : t('out.today.chest') } : {}),
      ...(open && others ? { pick: spectres.el } : {}),
      ...(open
        ? {
            go: {
              label: online ? t('dungeon.gather') : t('dungeon.goSolo'),
              test: tier === 1 ? 'crypt-go' : `crypt${tier}-go`,
              run: () => go.crypt(tier, spectres.get()),
              note: claimed ? t('out.note.xpOnly') : online ? t('out.note.party', { min: DUNGEON.minLive, max: DUNGEON.maxHeroes }) : '',
            },
          }
        : {}),
    });
  };
  crypt(1, dungeonOpen(s), t('out.st.den'), me.dungeonDay, t('out.crypt1.line'), [
    [t('out.rw.rare'), ''],
    [String(DUNGEON_REWARD.dust), t('out.rw.dust')],
    [String(DUNGEON_REWARD.xp), t('out.rw.xp')],
    [pct(DUNGEON_REWARD.relicBp), t('out.rw.relic')],
  ]);
  crypt(2, cryptTierOpen(s), groupReached(s, CRYPT_ASCUAS.minGroup) ? t('out.st.cryptBefore') : t('out.st.group', { n: CRYPT_ASCUAS.minGroup }), me.dungeonDay2, t('out.crypt2.line', { n: CRYPT_ASCUAS.rooms }), [
    [t('out.rw.rare'), ''],
    [String(CRYPT_ASCUAS.embers), t('out.rw.embers')],
    [String(CRYPT_ASCUAS.dust), t('out.rw.dust')],
    [String(CRYPT_ASCUAS.xp), t('out.rw.xp')],
    [pct(CRYPT_ASCUAS.relicBp), t('out.rw.relic')],
  ]);
  crypt(3, deepCryptOpen(s), groupReached(s, DEEP_CRYPT.minGroup) ? t('out.st.cryptBefore') : t('out.st.group', { n: DEEP_CRYPT.minGroup }), me.dungeonDay3, t('out.crypt3.line'), [
    [t('out.rw.perFloor'), t('out.rw.perFloorWhat')],
    [t('out.rw.rare'), t('out.rw.fromFloor2')],
  ]);

  // The Chasm: no bottom; the group's mark and the day's piece (docs/SIMA.md).
  const sOpen = simaOpen(s);
  const sRec = simaRecord(s, day);
  const sClaimed = me.simaDay === day;
  out.push({
    id: 'sima',
    cat: 'crypt',
    sigil: 'sima',
    kind: t('out.kind.sima', { max: SIMA.maxHeroes }),
    title: t('sima.title'),
    state: !sOpen ? 'lock' : sClaimed ? 'repeat' : 'todo',
    status: !sOpen ? t('out.st.cryptBefore') : sRec.best > 0 ? t('out.st.sima', { n: sRec.best }) : t('out.st.chest'),
    line: t('out.sima.line'),
    keys: ['sima'],
    rewards: [
      [t('out.rw.xpDust'), t('out.rw.perFloorSima')],
      [t('out.rw.rare'), t('out.rw.pieceDay')],
      [t('out.rw.rare'), t('out.rw.weekChest')],
    ],
    ...(sOpen ? { today: sClaimed ? t('out.today.claimed') : t('out.today.chest') } : {}),
    more: go.sima,
  });

  // Together: the cellars and the Furrows.
  const cOpen = cellarsOpen(s);
  const cClaimed = me.expeditionDay === day;
  out.push({
    id: 'cellars',
    cat: 'group',
    sigil: 'bell',
    kind: t('out.kind.cellars', { min: CELLARS.minHeroes, max: CELLARS.maxHeroes }),
    title: t('exp.title'),
    state: !cOpen ? 'lock' : cClaimed ? 'done' : 'todo',
    status: !cOpen ? t('out.st.group', { n: CELLARS.minGroup }) : cClaimed ? t('out.st.today') : t('out.st.chest'),
    line: t('out.cellars.line'),
    keys: ['cellars'],
    rewards: [
      [t('out.rw.rare'), ''],
      [String(CELLARS.reward.dust), t('out.rw.dust')],
      [String(CELLARS.reward.xp), t('out.rw.xp')],
      [pct(CELLARS.reward.relicBp), t('out.rw.relic')],
    ],
    ...(cOpen ? { today: cClaimed ? t('out.today.claimed') : t('out.today.chest') } : {}),
    more: go.cellars,
  });
  const mOpen = mazeOpen(s);
  const rec = mOpen ? mazeRecord(s, day) : null;
  const daysLeft = MAZE.weekDays - (day % MAZE.weekDays);
  const paid = !!rec?.paid.includes(svc.me);
  out.push({
    id: 'maze',
    cat: 'group',
    sigil: 'maze',
    kind: t('out.kind.maze', { max: MAZE.maxHeroes }),
    title: t('maze.title'),
    state: !mOpen ? 'lock' : paid ? 'done' : rec?.won ? 'wait' : 'todo',
    status: !mOpen ? t('out.st.group', { n: MAZE.minGroup }) : paid ? t('out.st.week') : rec?.won ? t('out.st.days', { n: daysLeft }) : t('out.st.centre'),
    line: !rec
      ? t('out.maze.line')
      : rec.won
        ? t('out.maze.lineWon', { names: rec.explorers.map((p) => nameOf(s, p)).join(', ') || '—' }) + (rec.explorers.includes(svc.me) ? '' : ` ${t('out.maze.notYou')}`)
        : t('out.maze.lineOpen', { k: rec.camps.length, n: MAZE.camps.length }),
    keys: ['maze'],
    rewards: [
      [t('out.rw.rare'), t('out.rw.weekly')],
      [String(MAZE.weekly.xp), t('out.rw.xp')],
      [String(MAZE.weekly.dust), t('out.rw.dust')],
      [String(MAZE.chest.dust), t('out.rw.corridor')],
    ],
    ...(mOpen && !rec?.won ? { today: t('out.today.centre', { n: daysLeft }) } : {}),
    more: go.furrows,
  });

  // Brann and Hilde's bets.
  const bets = betsOf(s);
  for (const b of story ? BETS : []) {
    const open = betOpen(s, b.id);
    const tries = bets.tries[b.id] ?? 0;
    const wonIt = bets.won.includes(b.id);
    out.push({
      id: `bet:${b.id}`,
      cat: 'bet',
      // The open gate pays a plaque, not a barrel.
      sigil: b.favor === 'both' ? 'plaque' : 'barrel',
      kind: t('out.kind.bet', { n: b.minGroup }),
      title: t(`bet.${b.id}.title`),
      state: !open ? 'lock' : wonIt ? 'repeat' : 'todo',
      status: !open ? t('out.st.group', { n: b.minGroup }) : wonIt ? t('out.st.won') : t('out.st.play'),
      line: `${t(`bet.${b.id}.rule`)} ${wonIt ? t(tries === 1 ? 'out.bet.wonOne' : 'out.bet.won', { n: tries }) : tries ? t(tries === 1 ? 'out.bet.triesOne' : 'out.bet.tries', { n: tries }) : ''}`.trim(),
      keys: [`bet:${b.id}`],
      rewards: wonIt ? [[String(Math.floor(BET.xp / 2)), t('out.rw.xpAgain')]] : [[String(BET.xp), t('out.rw.xp')], [t(b.favor === 'both' ? 'out.rw.plaque' : 'out.rw.barrel'), t('out.rw.forBastion')]],
      ...(open ? { go: { label: wonIt ? t('out.go.again') : t('out.go.bet'), test: `bet-go-${b.id}`, run: go.story } } : {}),
    });
  }
  return out;
};

/** How many outings still pay today: the number on the bar's button. */
export const outingsToDo = (svc: CampaignService): number => {
  const none = (): null => null;
  const nop = (): void => undefined;
  return outingsOf(svc, { map: nop, crypt: nop, story: nop, cellars: none, furrows: none, sima: none }).filter((o) => o.state === 'todo').length;
};

// ---------- the screen ----------

const RANK: Record<State, number> = { todo: 0, wait: 1, repeat: 2, done: 3, lock: 4 };
let filter: Cat | 'all' = 'all';
let who: 'me' | 'all' = 'me';
try {
  filter = (localStorage.getItem('somnhold.outings.filter') as Cat | 'all' | null) ?? 'all';
} catch {
  /* no storage: start on everything */
}

const whenText = (at: number, now: number): string => {
  const d = new Date(at);
  const n = new Date(now);
  const hm = d.toLocaleTimeString(getLang(), { hour: '2-digit', minute: '2-digit' });
  if (d.toDateString() === n.toDateString()) return t('out.when.today', { t: hm });
  const y = new Date(n);
  y.setDate(n.getDate() - 1);
  if (d.toDateString() === y.toDateString()) return t('out.when.yesterday', { t: hm });
  return d.toLocaleDateString(getLang(), { day: '2-digit', month: '2-digit' });
};

const faces = (s: CampaignState, pids: string[]): HTMLElement =>
  h(
    'span.out-faces',
    {},
    ...pids.map((pid) => {
      const p = s.players.find((x) => x.id === pid);
      return h('span.out-face', { style: `background:${p?.color ?? '#888'}`, title: p?.displayName ?? '—' }, (p?.displayName ?? '?').slice(0, 1).toUpperCase());
    }),
  );

/** One hero's (or the group's) tally across the card's keys. */
const tallyOf = (s: CampaignState, keys: string[], pid: string | null): OutingTally => {
  const src = pid ? s.outings?.by[pid] : s.outings?.group;
  const out: OutingTally = { n: 0, won: 0, last: 0, lastWon: 0, with: {} };
  for (const k of keys) {
    const x = src?.[k];
    if (!x) continue;
    out.n += x.n;
    out.won += x.won;
    out.last = Math.max(out.last, x.last);
    out.lastWon = Math.max(out.lastWon, x.lastWon);
    for (const [p, n] of Object.entries(x.with)) out.with[p] = (out.with[p] ?? 0) + n;
  }
  return out;
};

const mineLine = (svc: CampaignService, o: Outing): HTMLElement => {
  const s = svc.state;
  const m = tallyOf(s, o.keys, svc.me);
  if (!m.n) return h('span.out-mine', {}, t('out.mine.never'));
  const mates = Object.entries(m.with)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .slice(0, 4)
    .map(([p]) => p);
  return h(
    'span.out-mine',
    {},
    h('span', {}, t('out.mine.line', { n: m.n, won: m.won, when: whenText(m.last, svc.now()) })),
    mates.length ? h('span', {}, t('out.mine.with')) : h('span', {}, t('out.mine.alone')),
    mates.length ? faces(s, mates) : '',
  );
};

const statusChip = (o: Outing): HTMLElement => h(`span.out-status.${o.state}`, {}, o.state === 'lock' ? h('span', { html: ICONS.lock }) : '', o.status);

const rewardsRow = (o: Outing): HTMLElement => h('span.out-rewards', {}, ...o.rewards.map(([v, l]) => h('span.out-rw', {}, h('b', {}, v), l ? ` ${l}` : '')));

const card = (svc: CampaignService, o: Outing, open: (o: Outing) => void): HTMLElement =>
  h(
    `button.out-card.cat-${o.cat}${o.state === 'lock' ? '.locked' : ''}`,
    { 'data-test': `outing-${o.id}`, onclick: () => open(o) },
    sigil(o.sigil),
    h(
      'span.out-body',
      {},
      h('span.out-top', {}, h('span', {}, h('span.out-kind', {}, o.kind), h('h3', {}, o.title)), statusChip(o)),
      o.meter ? h('span.out-meter', { 'aria-hidden': 'true' }, ...Array.from({ length: o.meter[0] }, (_, i) => h(`span${i < o.meter![1] ? '.on' : i === o.meter![1] && o.meter![2] ? '.now' : ''}`))) : '',
      h('span.out-line', {}, o.line),
      o.keys.length ? mineLine(svc, o) : '',
      rewardsRow(o),
    ),
  );

const historyList = (svc: CampaignService, o: Outing): HTMLElement => {
  const s = svc.state;
  const runs: OutingRun[] = (s.outings?.recent ?? []).filter((r) => o.keys.includes(r.key) && (who === 'all' || r.pids.includes(svc.me))).slice(-12).reverse();
  if (!runs.length) return h('p.out-empty', {}, who === 'me' ? t('out.hist.noneMe') : t('out.hist.none'));
  const note = (r: OutingRun): string =>
    r.key === 'saga' || r.key.startsWith('side:') ? t('out.hist.chapter', { n: r.note ?? 0 }) : r.key === 'crypt3' && r.note ? t('out.hist.floors', { n: r.note }) : r.key === 'cellars' && r.won ? (r.note ? t('out.hist.falls', { n: r.note }) : t('out.hist.flawless')) : r.key.startsWith('inc') ? t('out.hist.tier', { n: r.key.slice(3) }) : '';
  return h(
    'ul.out-hist',
    {},
    ...runs.map((r) => {
      const extra = note(r);
      return h('li', {}, h('span.when', {}, whenText(r.at, svc.now())), h('span.party', {}, faces(s, r.pids), ' ', r.pids.map((p) => nameOf(s, p)).join(', ')), h(`span.res.${r.won ? 'w' : 'l'}`, {}, r.won ? t('out.hist.won') : t('out.hist.lost'), extra ? ` · ${extra}` : ''));
    }),
  );
};

const openSheet = (svc: CampaignService, o: Outing, host: HTMLElement): void => {
  host.querySelector('.out-scrim')?.remove();
  const s = svc.state;
  const me = tallyOf(s, o.keys, svc.me);
  const all = tallyOf(s, o.keys, null);
  const rate = (x: OutingTally): string => (x.n ? `${Math.round((x.won * 100) / x.n)} %` : '—');
  const close = (): void => {
    scrim.remove();
    document.removeEventListener('keydown', onKey);
  };
  const onKey = (ev: KeyboardEvent): void => {
    if (ev.key === 'Escape') close();
  };
  const hist = h('div', {}, o.keys.length ? historyList(svc, o) : '');
  const seg = h(
    'span.out-seg',
    { role: 'group', 'aria-label': t('out.hist.whose') },
    ...(['me', 'all'] as const).map((w) =>
      h(
        'button',
        {
          'aria-pressed': String(who === w),
          onclick: (ev: Event) => {
            who = w;
            for (const b of seg.querySelectorAll('button')) b.setAttribute('aria-pressed', String(b === ev.currentTarget));
            hist.replaceChildren(historyList(svc, o));
          },
        },
        w === 'me' ? t('out.hist.me') : t('out.hist.group'),
      ),
    ),
  );
  const more = o.more?.() ?? null;
  const sheet = h(
    `div.out-sheet.cat-${o.cat}`,
    { role: 'dialog', 'aria-modal': 'true', 'data-test': `outing-sheet-${o.id}`, onclick: (ev: Event) => ev.stopPropagation() },
    h('div.out-grab', {}, h('span.out-kind', {}, t(`out.cat.${o.cat}`)), h('button.out-x', { 'aria-label': t('out.close'), 'data-test': 'outing-close', onclick: close }, '✕')),
    h('div.out-sh-head', {}, sigil(o.sigil), h('div', {}, h('span.out-kind', {}, o.kind), h('h3', {}, o.title), statusChip(o))),
    h('p.out-sh-line', {}, o.line),
    o.go && !more && o.pick ? h('div.out-sec', {}, o.pick()) : '',
    o.go && !more ? h('div.out-cta', {}, h('button.btn.out-go', { 'data-test': o.go.test, onclick: () => (close(), o.go!.run()) }, o.go.label), o.go.note ? h('small', {}, o.go.note) : '') : '',
    o.keys.length
      ? h(
          'div.out-sec',
          {},
          h('h4', {}, t('out.sec.numbers')),
          h(
            'div.out-facts',
            {},
            h('div', {}, h('small', {}, t('out.fact.went')), h('b', {}, String(me.n))),
            h('div', {}, h('small', {}, t('out.fact.won')), h('b', {}, rate(me))),
            h('div', {}, h('small', {}, t('out.fact.group')), h('b', {}, rate(all)), h('small', {}, t('out.fact.runs', { n: all.n }))),
          ),
        )
      : '',
    h('div.out-sec', {}, h('h4', {}, t('out.sec.reward')), rewardsRow(o)),
    more ? h('div.out-sec.out-more', {}, more) : '',
    o.keys.length ? h('div.out-sec', {}, h('div.out-sec-head', {}, h('h4', {}, t('out.sec.history')), seg), hist) : '',
  );
  const scrim = h('div.out-scrim', { onclick: close }, sheet);
  host.append(scrim);
  document.addEventListener('keydown', onKey);
  (sheet.querySelector('.out-x') as HTMLElement | null)?.focus();
};

/** The screen. `open` names an outing to open straight away (a gathering's "join", a deep link). */
export const outingsPanel = (svc: CampaignService, go: OutingsGo, open?: string): HTMLElement => {
  const s = svc.state;
  const me = s.players.find((p) => p.id === svc.me) as PlayerState;
  const wrap = h('div.outings', { 'data-test': 'outings' });
  const all = outingsOf(svc, go);
  const byId = (id: string): Outing | undefined => all.find((o) => o.id === id);
  const show = (o: Outing): void => openSheet(svc, o, wrap);

  // Who you are, and where the group stands.
  wrap.append(
    h(
      'div.out-who',
      {},
      h('span.out-pill', {}, t('out.who.hero', { name: me.displayName, cls: t(`class.${me.hero.heroClass}`) }), ' ', h('b', {}, String(me.hero.level))),
      h('span.out-pill', {}, t('out.who.group'), ' ', h('b', {}, String(groupLevel(s, s.clock)))),
    ),
  );

  // Today: what still pays, what was cashed in, and who is gathering.
  const today = h('section.out-today', {}, h('div.out-head', {}, h('h2', {}, t('out.today.title')), h('small', {}, t('out.today.reset'))));
  const name = (pid: string): string => nameOf(s, pid);
  const rooms = (svc.presence()?.rooms ?? []).filter((r) => !r.started && r.kind !== 'wave' && !r.players.includes(svc.me));
  for (const r of rooms.slice(0, 2)) {
    const id = r.kind === 'crypt' ? (r.id.startsWith('crypt3') ? 'crypt3' : r.id.startsWith('crypt2') ? 'crypt2' : 'crypt1') : r.kind === 'expedition' ? 'cellars' : r.kind === 'maze' ? 'maze' : r.kind === 'saga' ? 'saga' : 'inc';
    const o = byId(id);
    today.append(
      h(
        'div.out-live',
        { 'data-test': 'outing-live' },
        h('span.pulse', { 'aria-hidden': 'true' }),
        h('p', {}, h('b', {}, r.players.map(name).join(', ') || name(r.leader)), ' ', t('out.live.at', { place: o?.title ?? '' })),
        o ? h('button.out-join', { onclick: () => show(o) }, t('out.live.join')) : '',
      ),
    );
  }
  const ledger = all.filter((o) => o.today && o.state !== 'lock').sort((a, b) => RANK[a.state] - RANK[b.state]);
  today.append(
    ledger.length
      ? h(
          'div.out-ledger',
          {},
          ...ledger.map((o) =>
            h(
              `button.out-tick${o.state === 'done' ? '.done' : ''}`,
              { 'data-test': `today-${o.id}`, onclick: () => show(o) },
              h(`span.mark.${o.state === 'done' ? 'ok' : 'todo'}`, { 'aria-hidden': 'true' }),
              h('span', {}, h('b', {}, o.title), h('small', {}, o.today as string)),
            ),
          ),
        )
      : h('p.out-empty', {}, t('out.today.none')),
  );
  wrap.append(today);

  // Filters and the cards.
  const list = h('div.out-list');
  const chips = h('nav.out-filters', { 'aria-label': t('out.filter') });
  const paint = (): void => {
    chips.replaceChildren(
      ...(['all', ...CATS] as const).map((c) => {
        const n = all.filter((o) => (c === 'all' || o.cat === c) && o.state === 'todo').length;
        return h(
          `button.out-f${c !== 'all' ? `.cat-${c}` : ''}`,
          {
            'aria-pressed': String(filter === c),
            'data-test': `outings-filter-${c}`,
            onclick: () => {
              filter = c;
              try {
                localStorage.setItem('somnhold.outings.filter', c);
              } catch {
                /* no storage */
              }
              paint();
            },
          },
          c !== 'all' ? h('i', { 'aria-hidden': 'true' }) : '',
          t(`out.cat.${c}`),
          n ? h('span.n', {}, String(n)) : '',
        );
      }),
    );
    const cats = filter === 'all' ? CATS : [filter];
    list.replaceChildren(
      ...cats.map((c) => {
        const items = all.filter((o) => o.cat === c).sort((a, b) => RANK[a.state] - RANK[b.state]);
        return items.length ? h(`section.out-group.cat-${c}`, {}, h('h2.out-group-title', {}, h('i', { 'aria-hidden': 'true' }), t(`out.cat.${c}`)), h('div.out-grid', {}, ...items.map((o) => card(svc, o, show)))) : '';
      }),
    );
  };
  paint();
  wrap.append(chips, list);
  if (open) {
    const o = byId(open);
    if (o) queueMicrotask(() => show(o));
  }
  return wrap;
};
